import React, { useContext, useEffect, useState } from 'react';
import { Button, Input, IconButton } from '@indigo-ag/component-module';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { CreditConditional, UpdateCreditConditionalInput } from '@indigo-ag/schema';
import { creditConditionalsOptions } from 'shared';
import i18next from 'core/i18n';
import { ConditionalsValidationRow } from './ConditionalsTable.config';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import SelectedConditionalsList, { ConditionalPayload } from './SelectedConditionalsList';
import {
  FormContainer,
  FormRow,
  ConditionalSelect,
  ActionsWrapper,
  Modal,
  ConditionalButton
} from './ConditionalsEdit.styles';
import { useSaveCreditConditional } from './hooks/useSaveCreditConditional';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';

const MIN_LENGTH_OTHER_CONDITIONAL = 3;

type SelectedConditional = CreditConditional | string;

interface ConditionalsEditProps {
  roundId: string;
  data: ConditionalsValidationRow[];
  alreadyAdded?: string[];
}

const ConditionalsEdit = ({ roundId, data, alreadyAdded }: ConditionalsEditProps) => {
  const [open, setOpen] = useState(false);
  const [selectedConditional, setSelectedConditional] = useState<SelectedConditional | null>(null);
  const [conditionalsList, setConditionalsList] = useState<ConditionalsValidationRow[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [errors, setErrors] = useState<{ conditionals?: string; other?: string }>({});
  const [otherConditionalSelected, setOtherConditionalSelected] = useState(false);
  const [deletedConditionals, setDeletedConditionals] = useState<UpdateCreditConditionalInput[]>([]);

  const { agFinancingId } = useContext(FinanceProcessContext);

  const { saveCreditConditional, loading } = useSaveCreditConditional();

  useEffect(() => {
    if (open) {
      setConditionalsList(data);
    }
  }, [data, open]);

  const handleClose = () => {
    setOpen(false);
    setConditionalsList([]);
    setSelectedConditional(null);
    setOtherConditionalSelected(false);
    setInputValue('');
    setDeletedConditionals([]);
  };

  const handleSave = () => {
    const conditionalsInput = [
      ...conditionalsList.map<UpdateCreditConditionalInput>(item => ({
        id: typeof item.id === 'string' ? item.id : undefined,
        name: item.conditional,
        deleted: false,
        validated: item.validated,
        observation: item.observation
      })),
      ...deletedConditionals
    ];

    saveCreditConditional({
      agFinancingId,
      roundId,
      selectedConditionals: conditionalsInput,
      callback: handleClose
    });
  };

  const validateOtherConditional = (value: string) => {
    if (value.length === 0) {
      setErrors({ other: i18next.t('creditConditionals-otherRequired') });
      return false;
    }
    if (value.length < MIN_LENGTH_OTHER_CONDITIONAL) {
      setErrors({
        other: i18next.t('creditConditionals-minLengthOtherConditional', { minLength: MIN_LENGTH_OTHER_CONDITIONAL })
      });
      return false;
    }
    setErrors(current => ({ ...current, other: undefined }));
    return true;
  };

  const handleAddConditional = () => {
    if (otherConditionalSelected && !validateOtherConditional(inputValue)) return;

    if (!selectedConditional) return;

    const selected = conditionalsList.find(item => item.conditional === selectedConditional);
    if (selected) return;

    const newConditional = otherConditionalSelected ? inputValue : selectedConditional;

    setConditionalsList(current => [
      ...current,
      { id: current.length + 1, conditional: newConditional, observation: '', validated: false }
    ]);
    setSelectedConditional(null);
    setInputValue('');
    setOtherConditionalSelected(false);
  };

  const handleChangeItem = (item: ConditionalPayload) => {
    setConditionalsList(current => {
      const index = current.findIndex(conditional => conditional.id === item.id);
      if (index === -1) return current;
      const updated = [...current];
      updated[index] = { ...item };
      return updated;
    });
  };

  const handleDeleteItem = (item: ConditionalsValidationRow) => {
    setConditionalsList(current => current.filter(conditional => conditional.id !== item.id));

    if (typeof item.id !== 'number') {
      setDeletedConditionals(current => [
        ...current,
        {
          id: item.id.toString(),
          name: item.conditional,
          deleted: true,
          validated: item.validated,
          observation: item.observation
        }
      ]);
    }
  };

  const modalContent = (
    <FormContainer>
      <FormRow>
        <ConditionalSelect
          name="conditionals"
          label={i18next.t('creditConditionals-selectLabel')}
          value={selectedConditional}
          placeholder={i18next.t('creditConditionals-selectPlaceholder')}
          options={creditConditionalsOptions}
          onChange={e => {
            const selected = e.target.value as CreditConditional;
            setSelectedConditional(selected);
            setOtherConditionalSelected(selected === CreditConditional.OTHER);
          }}
          faIconBefore={faSearch}
          filterable
          noFilteredItemsMessage={i18next.t('noAvailableOptions')}
          disableNative
          portaledDropdown
        />
        <IconButton icon={faPlus} onClick={handleAddConditional} />
      </FormRow>
      {otherConditionalSelected && (
        <Input
          label={i18next.t('creditConditionals-otherInputLabel')}
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            validateOtherConditional(e.target.value);
          }}
          placeholder={i18next.t('creditConditionals-otherInputPlaceholder')}
          error={errors.other}
        />
      )}
      <SelectedConditionalsList
        items={conditionalsList}
        onChangeItem={handleChangeItem}
        onDeleteItem={handleDeleteItem}
        alreadyAdded={alreadyAdded}
      />
    </FormContainer>
  );

  const modalActions = (
    <ActionsWrapper>
      <Button onClick={handleClose} type="button" size="small" variant="secondary">
        {i18next.t('cancel')}
      </Button>
      <Button onClick={handleSave} type="submit" size="small" disabled={loading} busy={loading}>
        {i18next.t('save')}
      </Button>
    </ActionsWrapper>
  );

  return (
    <>
      <Modal
        open={open}
        onRequestClose={handleClose}
        title={i18next.t('editCreditConditionalsModalTitle')}
        renderContent={() => modalContent}
        renderActionArea={() => modalActions}
      />
      <ConditionalButton onClick={() => setOpen(true)}>{i18next.t('conditionalValidations-edit')}</ConditionalButton>
    </>
  );
};

export default ConditionalsEdit;
